import React, { Fragment, useState } from 'react';
import { bool, func, number, object, string } from 'prop-types';

import 'apps/billing/styles/components/manage-subscription-modal.scss';
import { resolve } from 'library/helpers/path-resolver';
import SubscriptionList from './subscription-seat-list/subscription-list';
import SelectManager from './select-manager';
import OneTimeChargeForm from './one-time-charge-form';
import ConfirmationPage from './confirmation-page';
import CompletionStep from './completion-step';
import PreviewInvoice from './preview-invoice';
import CancelSubscription from '../subscription-card/cancel-subscription';
import Logo from 'apps/hiring/components/logo';
import Notification, { Error as NotificationError, Success } from 'library/components/notification';
import Confirm from 'library/helpers/confirm';
import {
  activateSubscriptionMutation,
  createSubscriptionMutation,
  addUserToSeatMutation,
  addSeatsToSubscriptionMutation,
  createNodePolicyMutation
} from 'apps/billing/queries/subscription-mutation';
import {
  createChargeMutation,
} from 'apps/billing/queries/credit-card-mutation';

const ManageSubscriptionModal = (props) => {

  const [subscriptionStep, setSubscriptionStep] = useState(0);
  const [previousStep, setPreviousStep] = useState(null);

  const [selectedManagers, setSelectedManagers] = useState([]);
  const [currentSubscriptionInfo, setCurrentSubscriptionInfo] = useState(null);
  const [activatingSubscription, setActivatingSubscription] = useState(false);
  const [addSeatPath, setAddSeatPath] = useState(false);

  const [chargeAmount, setChargeAmount] = useState(null);
  const [monthlyAmount, setMonthlyAmount] = useState(null);
  const [token, setToken] = useState(null);

  const handleUpdateStep = (step) => {
    if (step === 0) {
      setActivatingSubscription(false);
      setAddSeatPath(false);
    }

    setPreviousStep(subscriptionStep);
    setSubscriptionStep(step);
  };

  const getCurrentSubscriptionInfo = () => {
    if (!currentSubscriptionInfo) {
      let totalSeats = resolve(props.graph_data, 'subscription_items/0/seats/results', []).length;

      let activeSeats = resolve(
        props.graph_data,
        'subscription_items/0/seats/results',
        []
      ).filter(e=> resolve(e, 'user/id', false)).length;

      return {totalSeats, activeSeats};
    }
    return currentSubscriptionInfo;
  };

  const handleActivateSubscription = async () => {
    if (!activatingSubscription) return;

    // if we're activating a trial, call trial-specific mutation
    if (props.subscription.status === 'trialing') {
      try {
        let response = await activateSubscriptionMutation();
        if (resolve(response, 'data/activate_subscription/success')) {
          Notification.new(
            {timeout: 5000},
            <Success>
              Subscripiton activated successfully!
            </Success>
          );
          setSubscriptionStep(4);
        } else {
          console.error(response);
          Notification.new(
            {timeout: 5000},
            <NotificationError>
              {resolve(response, 'data/activate_subscription/errors/messages', 'Something went wrong!')}
            </NotificationError>
          );
          setSelectedManagers([]);
          setAddSeatPath(false);
          setSubscriptionStep(0);
        }
      } catch (e) {
        console.error(e);
        setSelectedManagers([]);
        setAddSeatPath(false);
        setSubscriptionStep(0);
        Notification.new(
          {timeout: 5000},
          <NotificationError>
            {resolve(e, 'data/activate_subscription/errors/messages', 'Something went wrong!')}
          </NotificationError>
        );
      }
    } else {
      try {
        // query for the associated 'hiring' plan_id
        let plan_ids = [resolve(props.graph_data, 'plan/id', null)];
        if (!plan_ids[0]) throw Error('No valid plan returned.');
        // in this scenario, we are reactivating an expired or canceled subscription so
        // set trial_days to 0
        let response = await createSubscriptionMutation({ trial_days: 0, plan_ids: plan_ids, quantity: 1 });
        if (resolve(response, 'data/stripe_create_subscription/success')) {
          Notification.new(
            {timeout: 5000},
            <Success>
              Subscripiton created successfully!
            </Success>
          );
          setSubscriptionStep(4);
        } else {
          console.error(response);
          Notification.new(
            {timeout: 5000},
            <NotificationError>
              {resolve(response, 'data/stripe_create_subscription/errors/messages', 'Something went wrong!')}
            </NotificationError>
          );
          setSelectedManagers([]);
          setAddSeatPath(false);
          setSubscriptionStep(0);
        }
      } catch (e) {
        console.error(e);
        setSelectedManagers([]);
        setAddSeatPath(false);
        setSubscriptionStep(0);
        Notification.new(
          {timeout: 5000},
          <NotificationError>
            {resolve(e, 'data/stripe_create_subscription/errors/messages', 'Something went wrong!')}
          </NotificationError>
        );
      }
    }
  };

  const handleConfirmPath = async () => {
    //once confirm is clicked, we can charge, add seats and assign managers
    let success = await handlePayment();
    if (success) {
      handleActivateSubscription();
      handleAddManager();
    }
  };


  const handlePayment = async () => {
    // If there is an amount to pay (added seats?), we will try payment, then will proceed to
    // 'handleActivateSubscription' and 'handleAddManager'
    if (chargeAmount > 0) {
      try {
        let response = await createChargeMutation({token, chargeAmount});
        if (resolve(response, 'data/create_charge/success')) {
          Notification.new(
            {timeout: 5000},
            <Success>
              Payment successful!
            </Success>
          );
          return true;
        } else {
          console.error(response);
          Notification.new(
            {timeout: 5000},
            <NotificationError>
              {resolve(response, 'data/create_charge/errors/messages', 'Something went wrong!')}
            </NotificationError>
          );
          setSelectedManagers([]);
          setActivatingSubscription(false);
          setAddSeatPath(false);
          setSubscriptionStep(0);
          return false;
        }
      } catch (e) {
        console.error(e);
        Notification.new(
          {timeout: 5000},
          <NotificationError>
            {resolve(e, 'data/create_charge/errors/messages', 'Something went wrong!')}
          </NotificationError>
        );
        setSelectedManagers([]);
        setActivatingSubscription(false);
        setAddSeatPath(false);
        setSubscriptionStep(0);
        return false;
      }
    }

    return true;
  };

  const handleSubscriptionPath = async (isActivating) => {
    let info = getCurrentSubscriptionInfo();

    // if subscription is trialing OR we have more selected managers than
    // open seats, AND they clicked 'activate', send to payment step.
    if ((activatingSubscription || isActivating)
          && !addSeatPath
          && !selectedManagers.length > 0) {

      if(!resolve(props.subscription, 'owner/default_payment_source', false)) {
        // we need a billing method on file to activate subscription
        setSubscriptionStep(1);
        return;
      } else {
        let message = 'Use default billing method? ';
        // if they accept using default billing method, send to confirm page
        // otherwise send to one-time payment form to create a token
        if (await Confirm('Payment', { children: message })) {
          setSubscriptionStep(3);
          return;
        } else {
          setSubscriptionStep(1);
          return;
        }
      }
    }

    // if we need to charge for extra managers and assign them to seats,
    // and the subscr is not a trial, OR if we need to charge for a new seat
    // in an active subscription, run through charge flow.
    if (((info.totalSeats - info.activeSeats) < selectedManagers.length || addSeatPath)
      && props.subscription.status !== 'trialing') {

      // check to see if there is a default payment source enabled? If not, go straight to OneTimeChargeForm
      if(!resolve(props.subscription, 'owner/default_payment_source', false)) {
        setSubscriptionStep(1);
      } else {
        let message = 'Use default billing method? ';
        if (await Confirm('Payment', { children: message })) {
          setSubscriptionStep(3);
        } else {
          setSubscriptionStep(1);
        }
      }
    } else {
      // no payments today, this is an active-state subscripiton assigning users
      // to seats that are open, or adding seats to a trial.
      setSubscriptionStep(3);
    }
  };

  const handleAddManager = async () => {
    // handle adding mgrs to existing seats after adding new seats
    let subscriptionItemId = Number(resolve(props.graph_data, 'subscription_items/0/id', 0));
    let info = getCurrentSubscriptionInfo();

    // this is the 'adding a seat without a mgr assigned' path
    if (!selectedManagers.length > 0 && addSeatPath) {
      let quantity = info.totalSeats + 1;
      try {
        let response = await addSeatsToSubscriptionMutation({ subscriptionItemId, quantity });
        if (resolve(response, 'data/add_seats_to_subscription_item/success')) {
          Notification.new(
            {timeout: 5000},
            <Success>
              Seat successfully added!
            </Success>
          );
          setSelectedManagers([]);
          setSubscriptionStep(4);
          return;
        } else {
          console.error(response);
          Notification.new(
            {timeout: 5000},
            <NotificationError>
              {resolve(response, 'data/add_seats_to_subscription_item/errors/messages', 'Something went wrong!')}
            </NotificationError>
          );
          setSelectedManagers([]);
          setAddSeatPath(false);
          setSubscriptionStep(2);
          props.reload_graph(true, true);
          return;
        }
      } catch (e) {
        console.error('Erroring:', e);
        Notification.new(
          {timeout: 5000},
          <NotificationError>
            {resolve(e, 'data/errors/messages', 'Something went wrong!')}
          </NotificationError>
        );
        setSelectedManagers([]);
        setAddSeatPath(false);
        setSubscriptionStep(2);
        props.reload_graph(true, true);
        return;
      }
    }

    let activeDelta = info.totalSeats - info.activeSeats;
    if (!(activeDelta >= selectedManagers.length) || addSeatPath ) {
      let quantity;
      try {
        if (addSeatPath) {
          quantity = info.totalSeats + 1;
        } else {
          quantity = info.totalSeats + (selectedManagers.length - activeDelta);
        }

        let response = await addSeatsToSubscriptionMutation({ subscriptionItemId, quantity });

        if (resolve(response, 'data/add_seats_to_subscription_item/success')) {
          Notification.new(
            {timeout: 5000},
            <Success>
              Seat successfully added!
            </Success>
          );
          setSelectedManagers([]);
          setSubscriptionStep(4);
        } else {
          console.error(response);
          Notification.new(
            {timeout: 5000},
            <NotificationError>
              {resolve(response, 'data/add_seats_to_subscription_item/errors/messages', 'Something went wrong!')}
            </NotificationError>
          );
          setSelectedManagers([]);
          props.reload_graph(true, true);
          return;
        }
      } catch (e) {
        console.error('Erroring:', e);
        Notification.new(
          {timeout: 5000},
          <NotificationError>
            {resolve(e, 'data/errors/messages', 'Something went wrong!')}
          </NotificationError>
        );
        setSelectedManagers([]);
        props.reload_graph(true, true);
        return;
      }
    }

    for (const user of selectedManagers) {
      let userId = Number(user.id);
      let orgId = resolve(props.graph_data, 'organization/id', 0);
      let subscriptionItemId = resolve(props.graph_data, 'subscription_items/0/id', 0);

      try {
        let response = await addUserToSeatMutation({ userId, subscriptionItemId});

        if (resolve(response, 'data/add_user_to_seat/success')) {

          let policyResponse = await createNodePolicyMutation({ userId, orgId });
          if (resolve(policyResponse, 'data/create_node_policy/success')) {
            Notification.new(
              {timeout: 5000},
              <Success>
                Manager successfully assigned to seat!
              </Success>
            );
            setSelectedManagers([]);
            setSubscriptionStep(4);
          } else {
            console.error(policyResponse);
            Notification.new(
              {timeout: 5000},
              <NotificationError>
                {resolve(policyResponse, 'data/create_node_policy/errors/messages', 'Something went wrong!')}
              </NotificationError>
            );
            setSelectedManagers([]);
            props.reload_graph(true, true);
          }

        } else {
          console.error(response);
          Notification.new(
            {timeout: 5000},
            <NotificationError>
              {resolve(response, 'data/add_user_to_seat/errors/messages', 'Something went wrong!')}
            </NotificationError>
          );
          setSelectedManagers([]);
          props.reload_graph(true, true);
        }
      } catch (e) {
        console.error(e);
        Notification.new(
          {timeout: 5000},
          <NotificationError>
            {resolve(e, 'data/errors/messages', 'Something went wrong!')}
          </NotificationError>
        );
        setSelectedManagers([]);
        props.reload_graph(true, true);
      }
    }
  };

  // make invoice_preview query for charge amount changes
  const getConfirmationContent = () => {
    let info = getCurrentSubscriptionInfo();

    // if we're not activating a subscription AND we're either trialing OR
    // [there are more open seats than manager selections AND we're not adding a seat],
    // display the base confirmation message
    if (!activatingSubscription
      && (props.subscription.status === 'trialing' || (info.totalSeats - info.activeSeats) >= selectedManagers.length && !addSeatPath) ) {
      return (
        <Fragment>
          <div className="base-confirm">
            <p>You are {selectedManagers.length ? `assigning access` : `adding a seat `} for {selectedManagers.length ? `${selectedManagers.length} new manager(s) on` : `` } your subscription.</p>
            {props.subscription.status !== 'trialing' && (
              <p>{`If any seats are empty at the end of the cycle, we will remove them and update the subscription, but if the seat(s) are filled, you will be charged the full monthly billing amount.`}</p>
            )}
            <p>By clicking confirm, you agree to grant access to this product.</p>
          </div>
        </Fragment>
      );
    }

    // otherwise, the changes require 'preview_invoice' service to be called
    // in order to display the relevant information
    let quantity;
    let activeDelta = (info.totalSeats - info.activeSeats);
    // if a trial is being activated, quantity is the curent total seat count
    // otherwise if a canceled or expired subscripiton is being activated, qty = 1
    // otherwise it's the curent total seat count + any selections not covered by open seats
    if (activatingSubscription && props.subscription.status === 'trialing') {
      quantity = info.totalSeats;
    } else if (addSeatPath && !selectedManagers.length) {
      quantity = info.totalSeats + 1;
    } else if (activatingSubscription) {
      quantity = 1;
    } else {
      quantity = (selectedManagers.length - activeDelta) + info.totalSeats;
    }

    return (
      <PreviewInvoice
        sutscriptionStatus={props.subscription.status}
        activeDelta={activeDelta}
        quantity={quantity}
        selectedManagers={selectedManagers}
        setChargeAmount={setChargeAmount}
        setMonthlyAmount={setMonthlyAmount}
        activatingSubscription={activatingSubscription}
        addSeatPath={addSeatPath}
        trialEnding={activatingSubscription && props.subscription.status === 'trialing'}
      />
    );
  };

  // refactor this retrieval method with getters
  const manageSubscriptionFlow = [
    // base seat list page
    <SubscriptionList
      key="0"
      {...props}
      updateStep={handleUpdateStep}
      getCurrentSubscriptionInfo={getCurrentSubscriptionInfo}
      setCurrentSubscriptionInfo={setCurrentSubscriptionInfo}
      setAddSeatPath={setAddSeatPath}
      setActivatingSubscription={setActivatingSubscription}
      handleSubscriptionPath={handleSubscriptionPath}
    />,
    // stripe credit card form
    <OneTimeChargeForm
      key="1"
      {...props}
      setToken={setToken}
      updateStep={handleUpdateStep}
      addSeatPath={addSeatPath}
      activatingSubscription={activatingSubscription}
    />,
    // add seat to subscription
    <SelectManager
      addType='Employee'
      addSeatPath={addSeatPath}
      activatingSubscription={activatingSubscription}
      key='2'
      graph_data={props.graph_data}
      setSelectedManagers={setSelectedManagers}
      exclude={resolve(props.graph_data, 'subscription_items/0/seats/results', []).filter(e => resolve(e, 'user/id', false) !== false)}
      updateStep={handleUpdateStep}
      handleSubscriptionPath={handleSubscriptionPath}
    />,
    // confirm step after selecting Managers, before attemtping payment
    <ConfirmationPage
      key="3"
      onComplete={handleConfirmPath}
      currentStep={subscriptionStep}
      confirmText="CONFIRM"
      noReject
    >
      {getConfirmationContent()}
    </ConfirmationPage>,
    // successfully did all the things!
    <ConfirmationPage
      key="4"
      onComplete={() => {
        props.reload_graph(true, true);
      }}
      currentStep={subscriptionStep}
      confirmText="Ok"
      noReject
    >
      <CompletionStep {...props} />
    </ConfirmationPage>,
    <CancelSubscription
      key="5"
      subscription={props.subscription}
      updateStep={handleUpdateStep}
      {...props}
    />
  ];

  const getSubscriptionDetailsHeader = () => {
    let info = getCurrentSubscriptionInfo();

    let quantity;
    let activeDelta = (info.totalSeats - info.activeSeats);
    // if a trial is being activated, quantity is the curent total seat count
    // or if in the addSeat path, or if a canceled or expired subscripiton is being activated, qty = 1
    // otherwise it's any selections not covered by open seats
    if (activatingSubscription && props.subscription.status === 'trialing') {
      quantity = info.totalSeats;
    } else if (addSeatPath || activatingSubscription) {
      quantity = 1;
    } else {
      quantity = (selectedManagers.length - activeDelta);
    }

    return (
      <div className="subscription-modal-info">
        <div className="product-heading">
          <Logo className="modal-logo-icon" width={'50px'} height={'50px'} />
          <div>
            <span>PAIRIN Hiring</span>
            <span>Subscription</span>
          </div>
        </div>
        <div className="subscription-details">
          <div className="row-item">
            <span>{monthlyAmount ? `New monthly charge` : `Monthly charge`}</span>
            <span>
              {monthlyAmount ? `$${monthlyAmount.toLocaleString()}/month`
                : `$${props.monthlyCharge.toLocaleString()}/month`
              }
            </span>
          </div>
          {
            !chargeAmount && (props.subscription.status === 'trialing' || props.subscription.status === 'active') ?
              (
                <div className="row-item">
                  <span>{props.subscription.status === 'trialing' ?  `Trial end date` : `Next billing date`}</span>
                  <span>{props.subscription.current_period_end}</span>
                </div>
              ) : null
          }
          {
            chargeAmount ? (
              <Fragment>
                <div className="row-item">
                  <span>Purchases Today</span>
                  <span>{`Manager's Access - $${resolve(props.graph_data.subscription_items[0], 'price_per_seat', 0)} x (${quantity})`}</span>
                </div>
                <div className="row-item">
                  <span>Total due today</span>
                  <span>{`$${chargeAmount.toLocaleString()}`}</span>
                </div>
              </Fragment>
            ) : null
          }
        </div>
      </div>
    );
  };

  const getSubscriptionFooter = () => (
    previousStep !== null
    && previousStep !== subscriptionStep
    && (subscriptionStep !== 0 && subscriptionStep !== 4)
  ) ? (
      <div className="manage-subscription-footer">
        <button onClick={() => handleUpdateStep(previousStep)}>BACK</button>
      </div>
    ) : null;

  const handleClose = () => {
    props.reload_graph(true, true);
    props.onClose();
  };

  return (
    <div className="hiring-modal manage-subscription">
      <div className="hiring-modal-header">
        <div className="hiring-modal-header-inner">
          <p>Subscription</p>
          <a onClick={handleClose} href="javascript:void(0)">×</a>
        </div>
        <svg className="hiring-modal-header-svg" width="100%" height="20px" viewBox="0 0 100 50" preserveAspectRatio="none">
          <path className="hiring-modal-header-svg-first" fill="currentColor" strokeWidth="0" d={`M0,0 100,0 100,50 0,0`}></path>
          <path className="hiring-modal-header-svg-second" fill="currentColor" strokeWidth="0" d={`M0,0 100,0 0,50 0,0`}></path>
        </svg>
      </div>
      <div className="hiring-modal-body subscription-modal-body">
        {subscriptionStep !== 5 ? getSubscriptionDetailsHeader() : null}
        {manageSubscriptionFlow[subscriptionStep]}
      </div>
      {getSubscriptionFooter()}
    </div>
  );
};

export default ManageSubscriptionModal;

ManageSubscriptionModal.propTypes = {
  graph_data: object,
  reload_graph: func,
  onClose: func,
  subscription: object,
  monthlyCharge: string,
  totalSeats: number,
  activeSeats: number,
  activatingSubscription: bool,
  setActivatingSubscription: func
};
